import { formatPhoneNumber } from '../../utils/phoneFormatter';
import { exportToExcelClient } from '../../utils/exportUtils';
import { getExportHeaderFillHex, hexToRgb } from '../../shared/utils/exportTheme';
import { registerOpenSansForPdf } from '../../shared/utils/pdfFonts';

const userNeedsPasswordSetup = (user) => (
  user.is_active
  && user.email_verified
  && (user.password_reset_required === true || user.password_reset_required === 1)
);

export const getUserStatusLabel = (user) => {
  if (userNeedsPasswordSetup(user)) return 'Password Setup Pending';
  if (!user.is_active && !user.email_verified) return 'Pending Verification';
  if (!user.is_active && user.email_verified) return 'Deactivated';
  if (user.is_active) return 'Active';
  return 'Unknown';
};

/** Plain-text department value for export (no emoji icons — PDF/fonts cannot render them). */
export const formatDepartmentExportValue = (userDepts = []) => {
  if (!userDepts?.length) return '';
  return userDepts.map((dept) => dept.name).filter(Boolean).join(', ');
};

const formatExportDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString();
};

const formatExportedAtUtc = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  const day = d.getUTCDate().toString().padStart(2, '0');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[d.getUTCMonth()];
  const year = d.getUTCFullYear();
  const hours = d.getUTCHours().toString().padStart(2, '0');
  const minutes = d.getUTCMinutes().toString().padStart(2, '0');
  return `${day} ${month} ${year}, ${hours}:${minutes} UTC`;
};

const sanitizePdfText = (value) => (
  String(value ?? '')
    // Strip emoji / pictographs that standard PDF fonts cannot render.
    .replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, '')
    .replace(/\s+/g, ' ')
    .trim()
);

export const buildUserExportMeta = (context = {}, rowCount = 0) => {
  const role = context.role || '';
  let locationLabel = context.locationLabel || '';
  if (!locationLabel) {
    if (role === 'superadmin') {
      const selected = context.selectedLocationIds || [];
      if (selected.length === 1) {
        locationLabel = context.locations?.find((loc) => String(loc.id) === String(selected[0]))?.name || '1 location';
      } else if (selected.length > 1) {
        locationLabel = `${selected.length} locations`;
      } else {
        locationLabel = 'All locations';
      }
    } else if (context.locationName) {
      locationLabel = context.locationName;
    }
  }

  return {
    title: 'Users Report',
    organizationName: context.organizationName || '',
    locationLabel,
    exportedAt: formatExportedAtUtc(),
    rowCount,
    rowCountLabel: 'Total Users',
  };
};

export const buildUserExportRow = (user, { locations = [], getUserDepartmentsForDisplay }) => {
  const userDepts = getUserDepartmentsForDisplay(user);
  const locationNames = (user.location_ids || [])
    .map((locId) => locations.find((loc) => loc.id === locId)?.name)
    .filter(Boolean);

  return {
    'Employee ID': user.employee_id || '',
    Username: user.username || '',
    'Full Name': user.full_name || '',
    Email: user.email || '',
    'Contact Number': user.contact_number ? formatPhoneNumber(user.contact_number) : '',
    Role: (user.role || '').replace(/_/g, ' '),
    Department: formatDepartmentExportValue(userDepts),
    Location: locationNames.join(', '),
    Status: getUserStatusLabel(user),
    'Email Verified': user.email_verified ? 'Yes' : 'No',
    'Password Setup Pending': userNeedsPasswordSetup(user) ? 'Yes' : 'No',
    'Created At': formatExportDate(user.created_at),
    'Updated At': formatExportDate(user.updated_at),
    'Last Login': formatExportDate(user.last_login_at),
  };
};

export const buildUserExportRows = (users, selectedHeaders, context) => {
  const fullRows = users.map((user) => buildUserExportRow(user, context));
  return fullRows.map((row) => {
    const filtered = {};
    selectedHeaders.forEach((header) => {
      filtered[header] = row[header] ?? '';
    });
    return filtered;
  });
};

export const exportUsersToExcel = async (users, selectedHeaders, context) => {
  const rows = buildUserExportRows(users, selectedHeaders, context);
  if (rows.length === 0) {
    throw new Error('No users to export');
  }
  const meta = buildUserExportMeta(context, rows.length);
  const filename = `users_${new Date().toISOString().slice(0, 10)}.xlsx`;
  await exportToExcelClient(rows, filename, { sheetName: 'Users', meta });
  return rows.length;
};

const PDF_MARGIN = 10;
const PDF_HEADER_FONT_SIZE = 7;
const PDF_BODY_FONT_SIZE = 6;
const PDF_LINE_HEIGHT = 3.4;
const PDF_CELL_PADDING = 1.4;
const PDF_FOOTER_HEIGHT = 8;
const PDF_BORDER_RGB = [180, 180, 180];
const PDF_ZEBRA_RGB = [245, 245, 245];

export const exportUsersToPdf = async (users, selectedHeaders, context) => {
  const rows = buildUserExportRows(users, selectedHeaders, context);
  if (rows.length === 0) {
    throw new Error('No users to export');
  }

  const meta = buildUserExportMeta(context, rows.length);
  const headerRgb = hexToRgb(getExportHeaderFillHex());
  const { jsPDF } = await import('jspdf');
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const { family: fontFamily } = await registerOpenSansForPdf(pdf);

  const pageHeight = pdf.internal.pageSize.getHeight();
  const pageWidth = pdf.internal.pageSize.getWidth();
  const usableWidth = pageWidth - PDF_MARGIN * 2;
  const colWidth = usableWidth / selectedHeaders.length;
  const maxTextWidth = Math.max(colWidth - PDF_CELL_PADDING * 2, 8);

  let y = PDF_MARGIN;

  const wrapCellText = (value) => {
    const sanitized = sanitizePdfText(value);
    if (!sanitized) return [''];
    return pdf.splitTextToSize(sanitized, maxTextWidth);
  };

  const drawReportChrome = () => {
    let chromeY = PDF_MARGIN;
    pdf.setFont(fontFamily, 'bold');
    pdf.setFontSize(12);
    pdf.setTextColor(17, 24, 39);
    pdf.text(meta.title, pageWidth / 2, chromeY, { align: 'center' });
    chromeY += 5;

    const contextParts = [
      meta.organizationName,
      meta.locationLabel,
      meta.exportedAt ? `Exported ${meta.exportedAt}` : '',
    ].filter(Boolean);
    if (contextParts.length) {
      pdf.setFont(fontFamily, 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(75, 85, 99);
      pdf.text(contextParts.join('  ·  '), pageWidth / 2, chromeY, { align: 'center' });
      pdf.setTextColor(0, 0, 0);
      chromeY += 4;
    }

    pdf.setFont(fontFamily, 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(31, 41, 55);
    pdf.text(`${meta.rowCountLabel}: ${meta.rowCount}`, pageWidth / 2, chromeY, { align: 'center' });
    pdf.setTextColor(0, 0, 0);
    return chromeY + 5;
  };

  const drawFooter = (pageNumber, pageCount) => {
    const footerY = pageHeight - 5;
    pdf.setFont(fontFamily, 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(75, 85, 99);
    const leftParts = [meta.title, meta.organizationName, meta.locationLabel].filter(Boolean);
    let left = leftParts.join(' · ');
    if (left.length > 85) left = `${left.slice(0, 82)}…`;
    pdf.text(left, PDF_MARGIN, footerY);
    pdf.text(`Page ${pageNumber} of ${pageCount}`, pageWidth / 2, footerY, { align: 'center' });
    const shortDate = (meta.exportedAt || '').split(',')[0] || '';
    pdf.text(shortDate, pageWidth - PDF_MARGIN, footerY, { align: 'right' });
    pdf.setTextColor(0, 0, 0);
  };

  const drawCellBorders = (top, height) => {
    pdf.setDrawColor(...PDF_BORDER_RGB);
    pdf.setLineWidth(0.2);
    pdf.rect(PDF_MARGIN, top, usableWidth, height);
    for (let i = 1; i < selectedHeaders.length; i += 1) {
      const x = PDF_MARGIN + i * colWidth;
      pdf.line(x, top, x, top + height);
    }
  };

  const drawHeaderRow = () => {
    const headerLines = selectedHeaders.map((header) => wrapCellText(header));
    const headerLineCount = Math.max(1, ...headerLines.map((lines) => lines.length));
    const headerHeight = headerLineCount * PDF_LINE_HEIGHT + PDF_CELL_PADDING * 2;
    const top = y;

    pdf.setFillColor(headerRgb.r, headerRgb.g, headerRgb.b);
    pdf.rect(PDF_MARGIN, top, usableWidth, headerHeight, 'F');

    pdf.setFont(fontFamily, 'bold');
    pdf.setFontSize(PDF_HEADER_FONT_SIZE);
    pdf.setTextColor(255, 255, 255);
    selectedHeaders.forEach((header, index) => {
      const x = PDF_MARGIN + index * colWidth + PDF_CELL_PADDING;
      const textY = top + PDF_CELL_PADDING + PDF_LINE_HEIGHT * 0.75;
      headerLines[index].forEach((line, lineIndex) => {
        pdf.text(line, x, textY + lineIndex * PDF_LINE_HEIGHT);
      });
    });

    drawCellBorders(top, headerHeight);
    pdf.setTextColor(0, 0, 0);
    pdf.setFont(fontFamily, 'normal');
    pdf.setFontSize(PDF_BODY_FONT_SIZE);
    y = top + headerHeight;
  };

  const drawBodyRow = (cellLines, zebra) => {
    const rowLineCount = Math.max(1, ...cellLines.map((lines) => lines.length));
    const rowHeight = rowLineCount * PDF_LINE_HEIGHT + PDF_CELL_PADDING * 2;
    const top = y;

    if (zebra) {
      pdf.setFillColor(...PDF_ZEBRA_RGB);
      pdf.rect(PDF_MARGIN, top, usableWidth, rowHeight, 'F');
    }

    pdf.setFont(fontFamily, 'normal');
    pdf.setFontSize(PDF_BODY_FONT_SIZE);
    pdf.setTextColor(0, 0, 0);
    selectedHeaders.forEach((header, index) => {
      const x = PDF_MARGIN + index * colWidth + PDF_CELL_PADDING;
      const textY = top + PDF_CELL_PADDING + PDF_LINE_HEIGHT * 0.75;
      cellLines[index].forEach((line, lineIndex) => {
        pdf.text(line, x, textY + lineIndex * PDF_LINE_HEIGHT);
      });
    });

    drawCellBorders(top, rowHeight);
    y = top + rowHeight;
  };

  y = drawReportChrome();
  drawHeaderRow();

  rows.forEach((row, rowIndex) => {
    const cellLines = selectedHeaders.map((header) => wrapCellText(row[header]));
    const rowLineCount = Math.max(1, ...cellLines.map((lines) => lines.length));
    const rowHeight = rowLineCount * PDF_LINE_HEIGHT + PDF_CELL_PADDING * 2;

    if (y + rowHeight > pageHeight - PDF_MARGIN - PDF_FOOTER_HEIGHT) {
      pdf.addPage();
      y = drawReportChrome();
      drawHeaderRow();
    }

    drawBodyRow(cellLines, rowIndex % 2 === 1);
  });

  const pageCount = pdf.internal.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    pdf.setPage(page);
    drawFooter(page, pageCount);
  }

  const filename = `users_${new Date().toISOString().slice(0, 10)}.pdf`;
  pdf.save(filename);
  return rows.length;
};
